import { Injectable } from '@angular/core';
import { TripType } from '@constants/home.constants';
import { SearchRequest, SearchRequestWitchTripType } from '@models/requests/search-request.model';
import { Store } from '@ngrx/store';
import { CookieStorageService } from '@services/cookie-storage/cookie-storage.service';
import { tryParseJson } from '@utils/json-utils';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PreviousSearchService {

  private lastSearch = new BehaviorSubject<SearchRequestWitchTripType | undefined>(undefined);

  constructor(
    public store: Store,
    private cookieStorage: CookieStorageService,
  ) { }

  setLastSearchCookie(
    searchAvailabilityInput: SearchRequest | null,
    searchInput: SearchRequest | null,
    tripType: TripType | null,
  ): void {
    if (tripType && tripType !== TripType.multiCity && searchAvailabilityInput) {
      let criteria = searchAvailabilityInput.criteria;
      // We compare searchInput and searchAvailabilityInput because
      // searchAvailabilityInput sometimes clears departure dates for low fare.
      // In that case we compare both criteria and save the dates from searchInput
      if (searchInput && criteria.length === searchInput.criteria.length) {
        criteria = criteria.map((searchAvailCrit, index) => {
          const searchInputCrit = searchInput.criteria[index];
          const areCriteriaSame = searchAvailCrit.origin === searchInputCrit?.origin &&
            searchAvailCrit.destination === searchInputCrit.destination;
          if (areCriteriaSame && (!searchAvailCrit.beginDate || !searchAvailCrit.beginDate.trim())) {
            const formattedBeginDate = DateTime.fromISO(searchInputCrit.beginDate).toISODate();
            return {
              ...searchAvailCrit,
              beginDate: formattedBeginDate ?? '',
            };
          }
          return searchAvailCrit;
        });
      }
      const departDate = searchAvailabilityInput.criteria[0]?.beginDate.split('T')[0];
      const search: SearchRequestWitchTripType = {...searchAvailabilityInput, criteria, tripType}
      this.cookieStorage.setObjectItem(
        CookieStorageService.lastSearchCookie,
        search,
        this.getLastSearchCookieExpiration(departDate ?? ''),
      );
    }
  }

  getLastSearchCookie(): void {
    const lastSearchCookie = this.cookieStorage.getItem(
      CookieStorageService.lastSearchCookie,
    );

    const lastSearch = (
      lastSearchCookie && lastSearchCookie !== 'undefined'
        ? tryParseJson(lastSearchCookie)
        : undefined
    ) as SearchRequestWitchTripType;
    this.lastSearch.next(lastSearch);
  }

  getLastSearchData(): Observable<SearchRequestWitchTripType | undefined> {
    return this.lastSearch.asObservable();
  }

  clearLastSearchData() {
    this.lastSearch.next(undefined);
  }

  private getLastSearchCookieExpiration(criteriaDate: string): Date {
    const beginDate = DateTime.fromJSDate(
      DateTime.fromISO(criteriaDate).toJSDate(),
    );

    let expirationDate = DateTime.local().plus({ days: 30 }).toJSDate();
    if (
      beginDate.startOf('day') <
      DateTime.local().plus({ days: 30 }).startOf('day')
    ) {
      expirationDate = beginDate.plus({ days: 1 }).startOf('day').toJSDate();
    }
    return new Date(expirationDate);
  }
}
